import { SearchOutlined } from "@ant-design/icons";
import { Button, Card, Col, Input, Row, Select, Table, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { salesApi } from "../../../services/api";
import { ApiPageRequest } from "../../../types/api.types";
import { TokenDistributionResponse } from "../../../types/sales.types";

const { Title } = Typography;
const { Option } = Select;

const TokenDistributionListPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TokenDistributionResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [filters, setFilters] = useState<TokenDistributionResponse>(
    {} as TokenDistributionResponse
  );
  const [tempFilters, setTempFilters] = useState<TokenDistributionResponse>(
    {} as TokenDistributionResponse
  );

  const fetchData = async (
    page: number = pageNumber,
    size: number = pageSize,
    filterData: TokenDistributionResponse = filters
  ) => {
    setLoading(true);
    try {
      const request: ApiPageRequest<TokenDistributionResponse> = {
        pageNumber: page - 1,
        pageSize: size,
        filter: filterData,
      };

      const response = await salesApi.getTokenDistributionList(request);
      setData(response.data);
      setTotal(response.totalElements);
    } catch (error) {
      console.error("Failed to fetch transaction history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilterChange = (
    field: keyof TokenDistributionResponse,
    value: string
  ) => {
    setTempFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleFilter = (filterData: TokenDistributionResponse) => {
    setFilters(filterData);
    fetchData(1, pageSize, filterData);
  };

  const columns: ColumnsType<TokenDistributionResponse> = [
    {
      title: "Sender",
      dataIndex: "senderName",
      width: 150,
    },
    {
      title: "Receiver",
      dataIndex: "receiverName",
      width: 150,
    },
    {
      title: "Tokens",
      dataIndex: "tokenAmount",
      width: 100,
    },
    {
      title: "Transaction ID",
      dataIndex: "transactionId",
      width: 150,
    },
    {
      title: "Type",
      dataIndex: "transactionType",
      width: 120,
    },
    {
      title: "Date",
      dataIndex: "createdAt" /* String value */,
      width: 150,
    },
  ];

  return (
    <Card>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={4}>Transaction History</Title>
        </Col>
        <Col span={24}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Filter by sender phone"
                onChange={(e) =>
                  handleFilterChange("senderPhone", e.target.value)
                }
                value={tempFilters.senderPhone}
                allowClear
                size="large"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Filter by receiver phone"
                onChange={(e) =>
                  handleFilterChange("receiverPhone", e.target.value)
                }
                value={tempFilters.receiverPhone}
                allowClear
                size="large"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Transaction Type"
                onChange={(value) => {
                  handleFilter({ ...tempFilters, transactionType: value });
                  setTimeout(() => {
                    handleFilterChange("transactionType", value);
                  }, 500);
                }}
                value={tempFilters.transactionType}
                allowClear
                size="large"
                style={{ width: "100%" }}
              >
                <Option value="Sent">Sent</Option>
                <Option value="Received">Received</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => handleFilter({ ...tempFilters })}
                shape="circle"
                size="large"
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Table
            columns={columns}
            dataSource={data}
            rowKey="transactionId"
            loading={loading}
            scroll={{ x: 1000, y: "calc(100vh - 300px)" }}
            pagination={{
              total,
              pageSize,
              current: pageNumber,
              onChange: (page, size) => {
                setPageNumber(page);
                setPageSize(size);
                fetchData(page, size);
              },
            }}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default TokenDistributionListPage;
